/**
 * Two invariants later edits must preserve:
 *
 * - `en` goes into `init()`, never `addResourceBundle` afterwards, so the store is
 *   populated at module load. A non-React module reading a label at import time
 *   gets a raw key back otherwise. This binds the fallback only: a translated
 *   locale has English to fall back on, which is what lets it arrive later.
 * - `useSuspense: false` lets the first render resolve a string with no fallback.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { admin } from './resources/en/admin';
import { agentBuilder } from './resources/en/agentBuilder';
import { analysis } from './resources/en/analysis';
import { approvalChains } from './resources/en/approvalChains';
import { audiences } from './resources/en/audiences';
import { briefs } from './resources/en/briefs';
import { chats } from './resources/en/chats';
import { common } from './resources/en/common';
import { contentStudio } from './resources/en/contentStudio';
import { contentTest } from './resources/en/contentTest';
import { knowledge } from './resources/en/knowledge';
import { projects } from './resources/en/projects';
import { springboard } from './resources/en/springboard';
import { tools } from './resources/en/tools';
import { trackers } from './resources/en/trackers';
import { validation } from './resources/en/validation';

// eslint-disable-next-line i18next/no-literal-string -- namespace identifier
export const defaultNS = 'common';

const en = {
  admin,
  agentBuilder,
  analysis,
  approvalChains,
  audiences,
  briefs,
  chats,
  common,
  contentStudio,
  contentTest,
  knowledge,
  projects,
  springboard,
  tools,
  trackers,
  validation,
} as const;

// Recursive because namespaces nest, so a flat constraint would miss inner keys.
type Localised<T> = { [K in keyof T]: T[K] extends string ? string : Localised<T[K]> };
type PartiallyLocalised<T> = {
  [K in keyof T]?: T[K] extends string ? string : PartiallyLocalised<T[K]>;
};

/**
 * A locale may add keys the base lacks (plural categories differ by language) but
 * may not omit one.
 */
export type LocaleResources = Localised<typeof en>;

/**
 * What a translated locale must satisfy. Weaker than `LocaleResources` on
 * purpose, so a translation can land one namespace at a time.
 *
 * This buys less than it looks like: every property is optional, and a lazily
 * loaded catalogue is checked against this only where `loadLocale` widens it, so
 * a key renamed to `loadingg` compiles. `locale-coverage.test.ts` is what
 * actually rejects that, and it is the only thing that does.
 */
export type PartialLocaleResources = PartiallyLocalised<typeof en>;

// `satisfies` rather than a type annotation: it checks the locale while leaving
// `typeof resources` literal, which is what `i18next.d.ts` derives the key union
// from. An annotation would widen the literals away and destroy key safety.
//
// Only `en` is registered up front. Every translated catalogue is roughly 120 KB
// gzipped, and shipping them in the entry chunk charged that to every user
// including the majority the language switcher never reaches, so they arrive
// through `loadLocale`. `resources/all.ts` is the static assembly the guard tests
// walk; nothing on the boot path may import it.
export const resources = { en } satisfies Record<string, PartialLocaleResources>;

void i18n.use(initReactI18next).init({
  resources,
  /* eslint-disable i18next/no-literal-string -- language codes */
  lng: 'en',
  fallbackLng: 'en',
  /* eslint-enable i18next/no-literal-string */
  defaultNS,
  // Derived, so adding a namespace to `en` cannot leave it unregistered here.
  ns: Object.keys(en),
  returnNull: false,
  interpolation: {
    // React already escapes values, so i18next must not escape again.
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
});

/**
 * One dynamic import per locale, so each catalogue is its own chunk. Keyed by the
 * codes in `SUPPORTED_LOCALE_CODES` minus `en`; `locales.test.ts` asserts the two
 * stay in step, or a locale reaches the switcher with no way to load it.
 */
// `Partial` so the lookup below is typed as it behaves: an unknown code yields
// `undefined`, which a plain `Record` would claim cannot happen.
const LOCALE_LOADERS: Partial<Record<string, () => Promise<PartialLocaleResources>>> = {
  es: () => import('./resources/es').then((module) => module.es),
  pt: () => import('./resources/pt').then((module) => module.pt),
};

export const LAZY_LOCALE_CODES = Object.keys(LOCALE_LOADERS);

const inFlight = new Map<string, Promise<void>>();

/**
 * Resolves once `code` can render, and is safe to call repeatedly: the promise is
 * memoised so a user clicking through the switcher fetches each catalogue once.
 * A rejection is evicted rather than cached, so a retry after a dropped
 * connection can still succeed.
 *
 * Resolves immediately for `en` and for any unknown code. Callers decide what an
 * unknown code means; this only promises that it did not load one.
 */
export function loadLocale(code: string): Promise<void> {
  const load = LOCALE_LOADERS[code];
  if (!load) return Promise.resolve();

  const existing = inFlight.get(code);
  if (existing) return existing;

  const pending = load()
    .then((bundle) => {
      for (const [namespace, table] of Object.entries(bundle)) {
        i18n.addResourceBundle(code, namespace, table);
      }
    })
    .catch((error: unknown) => {
      inFlight.delete(code);
      throw error;
    });

  inFlight.set(code, pending);
  return pending;
}

export default i18n;
